import React, { useEffect, Suspense } from "react";
import i18next from "i18next";
import {
  I18nextProvider,
  initReactI18next,
  useTranslation as useI18nTranslation,
  TFunction,
  UseTranslationOptions,
} from "react-i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";

export const i18n = i18next.createInstance();

export type I18nConfig = {
  /** have a common namespace used around the full app */
  lng?: "en" | "fr" | "es";
  ns: Array<string>;
  defaultNS?: string;
};

type Props = {
  children: React.ReactNode;
  config: I18nConfig;
  loadingTemplate?: React.ReactNode;
};

export const I18nProvider = ({ children, config, loadingTemplate }: Props): React.JSX.Element => {
  useEffect(() => {
    const lng = config.lng ?? "en";
    if (process.env.NODE_ENV === "test") {
      i18n.init({
        initImmediate: false,
        lng,
        fallbackLng: "en",
        ns: config?.ns,
        defaultNS: config?.defaultNS ?? "default",
        debug: false,
        interpolation: {
          escapeValue: false, // not needed for react!!
        },
        react: {
          useSuspense: false,
        },
      });
    } else {
      i18n
        // load translation using http -> see /public/locales (i.e. https://github.com/i18next/react-i18next/tree/master/example/react/public/locales)
        // learn more: https://github.com/i18next/i18next-http-backend
        // want your translations to be loaded from a professional CDN? => https://github.com/locize/react-tutorial#step-2---use-the-locize-cdn
        .use(Backend)
        // detect user language
        // learn more: https://github.com/i18next/i18next-browser-languageDetector
        .use(LanguageDetector)
        // pass the i18n instance to react-i18next.
        .use(initReactI18next)
        // init i18next
        // for all options read: https://www.i18next.com/overview/configuration-options
        .init({
          initImmediate: true,
          fallbackLng: "en",
          ns: config?.ns,
          defaultNS: config?.defaultNS ?? "default",
          debug: process.env.NODE_ENV !== "production",
          lng,
          interpolation: {
            escapeValue: false, // not needed for react as it escapes by default
          },
          backend: {
            loadPath: process.env.PUBLIC_URL + "/locales/{{lng}}/{{ns}}.json",
            allowMultiLoading: false,
            crossDomain: true,
            withCredentials: false,
          },
          react: {
            bindI18n: "languageChanged",
            nsMode: "fallback",
            useSuspense: true,
          },
        });
    }
  }, []);

  return (
    <Suspense fallback={loadingTemplate ?? <div>Loading...</div>}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </Suspense>
  );
};

export type UseTranslationResult = UseTranslationOptions & {
  ready: boolean;
  translate: (text: string, fallbackText?: string) => string;
  t: TFunction<string[], undefined>;
};

/**
 * this wrapper is used in place of handling missing key,
 * or just use key with underscore remove
 *
 * @param namespace : used by translate function get value from specific namespace
 * @returns t : i18n translate function, default
 *          translate : custom translate function to hadle missing key
 *                      [for libs used by apps that does not support i18n]
 *          ready: is i18n reay and available
 */
export const useTranslation = (namespace?: string): UseTranslationResult => {
  const { t, ready, ...options } = useI18nTranslation(namespace && [namespace]);
  /** if fallbackText is not passed and is needed, remove the underscores from text
   *
   *  Interpolation Docs : https://www.i18next.com/translation-function/interpolation#basic
   */
  const translate = (text: string, fallbackText?: string): string => {
    const fallback = fallbackText || text.split("_").join(" ");
    if (ready && namespace) {
      const translation = i18n.getResource && i18n.getResource(i18n.language, namespace, text);
      return ready && translation ? translation : fallback;
    }
    return fallback;
  };

  return { t, ready, translate, ...options };
};

export default i18n;
